<template>
  <div>
    <v-card class="my-1">
      <v-card-title>Export To Bank</v-card-title>
      <v-row 
        no-gutters
        class="px-4 mt-n4"
      >
        <v-col>
          <v-row 
            no-gutters
          >
            <v-col class="px-2">
              <v-autocomplete
                v-model="company"
                v-validate="'required'"
                label="Payroll Company"
                :loading="loading"
                :items="filteredCompanies"
                item-text="name"
                item-value="id"
                :error-messages="errors.collect('company')"
                data-vv-name="company"
                data-vv-as="payroll company"
                @change="loadCompanyBanks(); loadEmployees()"
              />
            </v-col>

            <v-col class="px-2">
              <v-autocomplete
                v-model="defaultCompanyBank"
                v-validate="'required'"
                disabled
                label="Report Type"
                :loading="loading"
                :items="companyBanks"
                item-text="payroll_bank.name"
                item-value="payroll_bank.id"
                :error-messages="errors.collect('defaultCompanyBank')"
                data-vv-name="defaultCompanyBank"
                data-vv-as="report type"
              />
            </v-col>

            <v-col class="px-2">
              <v-menu
                v-model="paymentDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="paymentDateDisplay"
                    v-validate="'required'"
                    label="Payment Date"
                    :error-messages="errors.collect('paymentDateDisplay')"
                    data-vv-name="paymentDateDisplay"  
                    data-vv-as="payment date"  
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="paymentDate"
                  @input="paymentDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-row 
            no-gutters
          >
            <v-col class="px-2">
              <v-autocomplete
                v-model="payrollPeriod"
                v-validate="'required'"
                :loading="loading"
                label="Payroll Period"
                :items="payrollPeriods"
                item-value="id"
                item-text="payroll_date"
                clearable
                :error-messages="errors.collect('payrollPeriod')"
                data-vv-name="payrollPeriod"
                data-vv-as="payroll period"
                hide-selected
                return-object
                @change="customFileName"
              />
            </v-col>

            <v-col class="px-2">
              <v-autocomplete
                v-model="payrollCycle"
                v-validate="'required'"
                clearable
                label="Payroll Cycle"
                hide-selected
                :loading="loading"
                :items="filteredPayrollCycles"
                :error-messages="errors.collect('payrollCycle')"
                :disabled="!payrollPeriod"
                data-vv-name="payrollCycle"
                item-value="id"
                item-text="payroll_cycle.name"
                data-vv-as="payroll cycle"
                return-object
                @change="loadEmployees()"
              />
            </v-col>

            <v-col class="px-2">
              <v-text-field
                v-model="filename"
                v-validate="'required'"
                label="Filename"
                :readonly="isPublicBank"
                :error-messages="errors.collect('filename')"
                data-vv-name="filename"
                data-vv-as="filename"
              >
                <template
                  v-if="isPublicBank"
                  v-slot:append
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div>Filename follows Public Bank's file naming convention:</div>
                    <div class="font-weight-bold">
                      [Account No.]PR[DDMMYY][Serial No.].xlsx
                    </div>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>

          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                :loading="progressing"
                color="primary"
                @click="generate"
              >
                generate
              </v-btn>
            </v-col>
          </v-row> 
        </v-col>
      </v-row>

      <v-row 
        class="pa-6"
        no-gutters
      >
        <v-col 
          cols="12"
        >
          <v-row>
            <v-card-title class="py-1 subtitle-1">
              Payment Method Via E-Wallet
            </v-card-title>
            <v-spacer />
            <v-card-title 
              v-if="company && payrollPeriod && payrollCycle"
              class="py-1 subtitle-2"
            >
              For {{ $moment(payrollPeriod.payroll_date).format('MMM YYYY') }} ( {{ payrollCycle.payroll_cycle.name }} )
            </v-card-title>
          </v-row>
          <v-divider />
          <v-data-table
            dense
            :headers="headers"
            :items="employees"
            :loading="tableLoading"
            hide-default-footer
            disable-pagination
            item-key="id"
            item-value="id"
            class="elevation-2"
            height="300px"
          >
            <template v-slot:item.amount="{ item }">
              {{ formatMoney(item.current_cycle_net_salary / 100) }}
            </template>

            <template v-slot:item.bank_account_number="{ item }">
              {{ item.payroll_cycle_employee.company_employee.employee.bank_account_number }}

              <v-tooltip 
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip 
                    color="red"
                    small
                    outlined
                    class="my-1"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="copyToClipboard(item)"
                    @mouseleave="resetTooltipText(item)"
                  >
                    <v-icon 
                      x-small
                    >
                      mdi-content-copy
                    </v-icon>
                  </v-chip>
                </template>

                <span>
                  <template v-if="item.isCopied">
                    ✓ Copied to clipboard!
                  </template>
                  <template v-else>
                    Click to copy {{ item.payroll_cycle_employee.company_employee.employee.bank_account_number }}
                  </template>
                </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card> 
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      payrollPeriods: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        progressing: false,
        tableLoading: false,
        company: null,
        filteredCompanies: [],
        progressbar: 0,
        companyBanks: [],
        payrollPeriod: null,
        filename: null,
        paymentDateMenu: false,
        paymentDate: null,
        payrollCycle: null,
        payrollCycles: [],
        employees: [],
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'payroll_cycle_employee.company_employee.employee.person.name',
          },
          {
            text: 'Department',
            align: 'left',
            sortable: true,
            value: 'payroll_cycle_employee.company_employee.employee.department.name',
          },
          {
            text: 'E-Wallet',
            align: 'left',
            sortable: true,
            value: 'payroll_cycle_employee.company_employee.employee.bank.name',
          },
          {
            text: 'Account Number',
            align: 'left',
            sortable: true,
            value: 'bank_account_number',
          },
          {
            text: 'Net Salary (RM)',
            align: 'right',
            sortable: false,
            value: 'amount',
          },       
        ],
      }
    },
    computed: {
      defaultCompanyBank () {
        let selected = this.companyBanks.find(companyBank => companyBank.is_default === 1)

        if (selected) {
          return selected.payroll_bank_id
        }

        return null
      },
      selectedReportType () {
        if (!this.defaultCompanyBank) return null
        return this.companyBanks.find(companyBank => companyBank.payroll_bank.id === this.defaultCompanyBank)
      },

      isPublicBank () {
        return (((this.selectedReportType || {}).payroll_bank || {}).bank_code || '').toUpperCase() === 'PBB'
      },

      // mirrors PublicBankExport::filename() in backend, payroll export always uses serial no. 01
      publicBankFilename () {
        let accountNo = ((this.selectedReportType || {}).bank_account_no || '').replace(/\D/g, '')
        if (!accountNo || !this.paymentDate) return null

        return accountNo + 'PR' + this.$moment(this.paymentDate, 'YYYY-MM-DD').format('DDMMYY') + '01.xlsx'
      },

      paymentDateDisplay () {
        return this.paymentDate ? this.$moment(this.paymentDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },

      filteredPayrollCycles () {
        if (!this.payrollPeriod) return []

        return this.payrollPeriods.find(payrollPeriod => payrollPeriod.id === this.payrollPeriod.id).payroll_cycle_transactions
      },
    },
    watch: {
      publicBankFilename (value) {
        if (this.isPublicBank) this.filename = value
      },
    },
    created () {
      this.loading = true

      this.filteredCompanies = this.companies.filter(company => {
        return company.id !== 'ALL'
      })

      this.loading = false
    },
    methods: {
      loadEmployees () {
        if (this.company && this.payrollCycle) {
          this.tableLoading = true 
        
          this.$api.exportToBanks().eWalletEmployees(this.prepareFormData())
            .then(res => {
              this.employees = _.orderBy(res.data, ['payroll_cycle_employee.company_employee.employee.person.name'], ['asc'])
              this.tableLoading = false
            })
        }
      },

      formatMoney (value) {
        if (!value) return 'RM 0.00'
        // v-money-like formatting
        return Number(value).toLocaleString('en-MY', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      },

      copyToClipboard (item) {
        const accountNo = item.payroll_cycle_employee.company_employee.employee.bank_account_number

        if (!accountNo) return

        if (navigator.clipboard) {
          navigator.clipboard.writeText(accountNo)
            .then(() => {
              this.setCopiedState(item)
            })
            .catch(() => {
              this.fallbackCopy(accountNo, item)
            })
        } else {
          this.fallbackCopy(accountNo, item)
        }
      },
      fallbackCopy (accountNo, item) {
        const textarea = document.createElement('textarea')
        textarea.value = accountNo
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        textarea.style.top = '-9999px'
        document.body.appendChild(textarea)

        textarea.focus()
        textarea.select()

        try {
          // @ts-ignore
          const successful = document.execCommand('copy')
          if (successful) {
            this.setCopiedState(item)
          }
        } catch (err) {
          // this.showToast('Failed to copy account number')
        }

        document.body.removeChild(textarea)
      },
      setCopiedState (item) {
        // Vue reactivity helper to set 'isCopied' dynamically
        this.$set(item, 'isCopied', true)

        // Automatically revert text back after 2 seconds if mouse stays hovered
        setTimeout(() => {
          this.$set(item, 'isCopied', false)
        }, 2000)
      },
      resetTooltipText (item) {
        // Revert state when user moves mouse away
        if (item.isCopied) {
          this.$set(item, 'isCopied', false)
        }
      },

      generate () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            let bankCode = ((this.selectedReportType || {}).payroll_bank || {}).bank_code.toLowerCase()

            this.$api.exportToBanks().export(bankCode, this.payrollPeriod.id, this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            }).then(res => {
              let blob = new Blob([res.data], { type: res.data.type })
              let link = document.createElement('a')
              link.href = window.URL.createObjectURL(blob)

              // Public Bank filename follows the bank's naming format, generated by backend
              let filename = bankCode === 'pbb' ? this.$filenameFromDisposition(res.headers['content-disposition']) : null

              link.setAttribute('download', filename || (this.filename + ' ' + ((res.data.type === 'text/plain') || (res.data.type === 'text/html') ? '.txt' : (res.data.type === 'text/csv') ? '.csv' : '.xlsx')))
              link.click()

              this.progressing = false
              this.progressbar = 0

              this.$snack({ message: 'Report downloaded successfully' })
            }).catch(() => {
              this.progressing = false

              this.$snack({ color: 'error', message: 'Error! Please contact support' })
            })
          })
      },

      loadCompanyBanks () {
        this.$api.companyBanks().show(this.company)
          .then(res => {
            this.companyBanks = _.orderBy(res.data, ['payroll_bank.name'], ['asc'])

            this.customFileName()
          })
      },

      prepareFormData () {
        return {
          company_uid: this.company,
          payment_date: this.paymentDate,
          file_name: this.filename,
          payroll_cycle_transaction_id: this.payrollCycle.id,
        }
      },

      customFileName () {
        if (!this.payrollPeriod) {
          this.payrollCycle = null
          this.employees = []
          return
        }

        if (this.filteredPayrollCycles && this.filteredPayrollCycles.length === 1) {
          this.payrollCycle = this.filteredPayrollCycles[0]
          
          this.loadEmployees()
        } else {
          this.payrollCycle = null
          this.employees = []
        }

        if (this.isPublicBank) {
          this.filename = this.publicBankFilename
        } else if (this.company) {
          let selectedCompany = this.companies.find(company => company.id === this.company)
          let selectedFormat = this.companyBanks.find(companyBank => companyBank.payroll_bank.id === this.defaultCompanyBank)
          let selectedPayrollPeriod = this.payrollPeriods.find(payrollPeriod => payrollPeriod.id === this.payrollPeriod.id)

          let shortenCompanyName = (selectedCompany.name).split(' ').map(letter => letter.charAt(0)).join('')
          this.filename = shortenCompanyName + '-' + ((selectedFormat || {}).payroll_bank || {}).bank_code + '-' + (this.payrollPeriod ? selectedPayrollPeriod.payroll_date : '')
        }

        return null
      },
    },
  }
</script>
